import React from 'react';
import styled from 'styled-components';

// Import your local images only
import burmaRuby from '../../assets/image/Burma Ruby.webp';
import starRuby from '../../assets/image/Star Ruby.jpeg';
import blueSapphire from '../../assets/image/Blue Sapphire.jpeg';
import colombianEmerald from '../../assets/image/Colombian Emerald.jpeg';
import padparadscha from '../../assets/image/Padparadscha.jpeg';

const PageContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 40px 20px;
`;

const HeroSection = styled.section`
  height: 60vh;
  min-height: 500px;
  background: linear-gradient(rgba(0,0,0,0.3), rgba(0,0,0,0.3)),
              url('https://images.unsplash.com/photo-1599643478518-a784e5dc4c8f?ixlib=rb-1.2.1&auto=format&fit=crop&w=1950&q=80');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  image-rendering: -webkit-optimize-contrast;
  image-rendering: crisp-edges;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: white;
  margin-bottom: 60px;
  border-radius: 20px;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0,0,0,0.3);
    z-index: 1;
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
`;

const HeroTitle = styled.h1`
  font-size: 64px;
  margin-bottom: 20px;
  letter-spacing: 3px;
  font-weight: 300;
  text-shadow: 2px 2px 4px rgba(0,0,0,0.3);
  
  span {
    color: #c4a484;
    font-weight: 400;
  }
  
  @media (max-width: 768px) {
    font-size: 42px;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 18px;
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.8;
  opacity: 0.95;
  text-shadow: 1px 1px 2px rgba(0,0,0,0.3);
`;

const SectionTitle = styled.h2`
  font-size: 42px;
  text-align: center;
  margin: 60px 0 40px;
  color: #1a2a3a;
  font-weight: 300;
  
  &:after {
    content: '';
    display: block;
    width: 80px;
    height: 2px;
    background: #c4a484;
    margin: 20px auto;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 30px;
  margin-top: 40px;
`;

const GemCard = styled.div`
  background: white;
  border-radius: 15px;
  overflow: hidden;
  box-shadow: 0 15px 30px rgba(0,0,0,0.1);
  transition: all 0.3s ease;
  position: relative;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 20px 40px rgba(0,0,0,0.15);
  }
`;

const GemImageWrapper = styled.div`
  width: 100%;
  height: 280px;
  overflow: hidden;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 50px;
    background: linear-gradient(to top, rgba(0,0,0,0.3), transparent);
    pointer-events: none;
  }
`;

const GemImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  object-position: center;
  image-rendering: -webkit-optimize-contrast;
  image-rendering: crisp-edges;
  background-color: #f8f9fa;
  transition: transform 0.5s ease;
  
  ${GemCard}:hover & {
    transform: scale(1.05);
  }
`;

const GemInfo = styled.div`
  padding: 25px;
`;

const GemName = styled.h3`
  font-size: 24px;
  color: #1a2a3a;
  margin-bottom: 10px;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const GemDetails = styled.p`
  color: #666;
  line-height: 1.6;
  margin-bottom: 15px;
  font-size: 14px;
`;

const GemProperties = styled.div`
  display: flex;
  gap: 15px;
  flex-wrap: wrap;
  margin-top: 15px;
  padding-top: 15px;
  border-top: 1px solid #f0f0f0;
`;

const Property = styled.span`
  background: #f8f9fa;
  padding: 5px 15px;
  border-radius: 20px;
  font-size: 12px;
  color: #555;
  border: 1px solid #e0e0e0;
  display: inline-flex;
  align-items: center;
  
  &:before {
    content: '✦';
    color: #c4a484;
    margin-right: 5px;
    font-size: 10px;
  }
`;

const RarityBadge = styled.span`
  background: ${props => {
    if (props.rarity === 'Rare') return '#ff4757';
    if (props.rarity === 'Very Rare') return '#9b59b6';
    if (props.rarity === 'Exceptional') return '#f39c12';
    return '#c4a484';
  }};
  color: white;
  font-size: 11px;
  padding: 4px 12px;
  border-radius: 20px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 600;
`;

const OriginBadge = styled.span`
  background: #1a2a3a;
  color: white;
  font-size: 12px;
  padding: 4px 10px;
  border-radius: 4px;
  display: inline-block;
  margin-top: 10px;
`;

const PreciousGems = () => {
  // Only the 5 gems with local images
  const gems = [
    {
      name: "Pigeon-Blood Ruby",
      image: burmaRuby,
      description: "The finest rubies, known for their intense 'pigeon blood' red color and exceptional fluorescence. These rubies are prized for their deep red hue and remarkable optical properties.",
      properties: ["Hardness: 9", "Color: Pigeon Blood Red", "Clarity: Eye-Clean"],
      origin: "High-mountain deposits",
      rarity: "Very Rare",
      price: "$15,000 - $100,000+ per carat"
    },
    {
      name: "Star Ruby",
      image: starRuby,
      description: "Rubies that display a rare asterism effect - a six-rayed star that moves across the surface when rotated. This phenomenon is caused by tiny rutile inclusions that reflect light in a star pattern.",
      properties: ["Hardness: 9", "Effect: Asterism", "Star: 6-Ray"],
      origin: "Alluvial deposits",
      rarity: "Rare",
      price: "$3,000 - $50,000 per carat"
    },
    {
      name: "Colombian Emerald",
      image: colombianEmerald,
      description: "The world's most coveted emeralds, prized for their deep green color and exceptional clarity. Sourced from renowned historic mines, these emeralds have been treasured for centuries.",
      properties: ["Hardness: 7.5-8", "Color: Vivid Green", "Origin: Renowned historic mines"],
      origin: "Renowned historic mines",
      rarity: "Exceptional",
      price: "$10,000 - $75,000+ per carat"
    },
    {
      name: "Blue Sapphire",
      image: blueSapphire,
      description: "Classic blue sapphires symbolizing wisdom and royalty. The finest examples come from historic highland and riverine deposits, displaying a rich velvety blue that has made them legendary.",
      properties: ["Hardness: 9", "Color: Cornflower Blue", "Birthstone: September"],
      origin: "Highland and riverine deposits",
      rarity: "Rare",
      price: "$5,000 - $60,000+ per carat"
    },
    {
      name: "Padparadscha Sapphire",
      image: padparadscha,
      description: "Extremely rare sapphire with a unique salmon-orange color, named after the lotus flower. These gems are among the most valuable sapphires, prized for their delicate blend of pink and orange.",
      properties: ["Hardness: 9", "Color: Salmon-Orange", "Rarity: Exceptional"],
      origin: "Coastal alluvial deposits",
      rarity: "Very Rare",
      price: "$15,000 - $50,000+ per carat"
    }
  ];

  return (
    <PageContainer>
      <HeroSection>
        <HeroContent>
          <HeroTitle>
            <span>Precious</span> Gems
          </HeroTitle>
          <HeroSubtitle>
            The world's most coveted gemstones • Pigeon-blood rubies • Vivid green emeralds • Velvety blue sapphires
          </HeroSubtitle>
        </HeroContent>
      </HeroSection>
      
      <SectionTitle>Our Precious Collection</SectionTitle>
      <Grid>
        {gems.map((gem, index) => (
          <GemCard key={index}>
            <GemImageWrapper>
              <GemImage src={gem.image} alt={gem.name} />
            </GemImageWrapper>
            <GemInfo>
              <GemName>
                {gem.name}
                <RarityBadge rarity={gem.rarity}>{gem.rarity}</RarityBadge>
              </GemName>
              <GemDetails>{gem.description}</GemDetails>
              <OriginBadge>📍 {gem.origin}</OriginBadge>
              <GemProperties>
                {gem.properties.map((prop, i) => (
                  <Property key={i}>{prop}</Property>
                ))}
              </GemProperties>

            </GemInfo>
          </GemCard>
          
        ))}
      </Grid>
    </PageContainer>
  );
};

export default PreciousGems;
